import { Code, ConnectError } from '@bufbuild/connect';

import api from '@/shared/api/api';
import {
  AgentActivity,
  AgentActivity_ActivityKind,
  AgentActivity_ActivityState,
  AgentArtifact,
  AgentArtifact_ArtifactAction,
  AgentArtifact_ArtifactKind,
  AgentThread,
  ConversationMessage,
  ConversationMessage_MessageRole,
  GetAgentThreadResp,
  PageContext,
  PageResource,
  RunContext,
  RunAgentResp,
  UserMessage,
  UserApproval,
  UserApproval_ApprovalDecision,
  UserFileBlock,
  UserMessageBlock,
  UserTextBlock,
} from '@/shared/api/protocol-ts/api/agent/svc_agent_pb';
import {
  CloseSessionReq,
  NotifyFileUploadFinishedReq,
  NotifyFileUploadStartedReq,
  StartUploadSessionReq,
} from '@/shared/api/protocol-ts/api/core/svc_upload_pb';

export type AgentCoreActor = {
  userID: string;
  language?: string;
  email?: string;
};

export type AgentThreadSubjectType =
  | 'patient'
  | 'report'
  | 'document'
  | 'encounter';

const THREAD_SUBJECT_TYPES: AgentThreadSubjectType[] = [
  'patient',
  'report',
  'document',
  'encounter',
];

export type AgentThreadSubject = {
  type: AgentThreadSubjectType;
  id: string;
};

// Wire values are strings; an unknown type must not enter the client as a
// subject — it would flow into pointer keys and list grouping.
const toAgentThreadSubject = (
  type: string,
  id: string,
): AgentThreadSubject | undefined =>
  THREAD_SUBJECT_TYPES.includes(type as AgentThreadSubjectType)
    ? { type: type as AgentThreadSubjectType, id }
    : undefined;

export const toProtoThreadSubjectFields = (
  subject?: AgentThreadSubject | null,
) => ({
  SubjectType: subject?.type ?? '',
  SubjectID: subject?.id ?? '',
});

export type AgentCoreContext = {
  /** Empty patient_id selects a general (patient-less) thread. */
  patient_id?: string;
  organization_id?: string;
};

export type AgentCorePageContext = {
  page: string;
  resources: { type: string; id: string }[];
  /** Entity this page's chat is bound to; absent = non-entity page. */
  subject?: AgentThreadSubject;
  /** URL path (no query/fragment) of the page the context describes. */
  path?: string;
  /** Client-rendered DOM snapshot (agent-browser-like compact tree). */
  dom?: string;
};

export type AgentCorePatientHeader = {
  id: string;
  organizationId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
};

export type AgentCoreStudySummary = {
  id: string;
  type: string;
  createdAt: string;
  updatedAt: string;
};

export type AgentCoreReportSummary = {
  id: string;
  type: string;
  status: string;
  sourceStudyIds: string[];
  createdAt: string;
  updatedAt: string;
};

export type AgentCoreConsultationSummary = {
  id: string;
  type: string;
  status: string;
  availableDocuments: string[];
  createdAt: string;
  updatedAt: string;
};

export type AgentCorePatientSnapshot = {
  patient: AgentCorePatientHeader;
  studies: AgentCoreStudySummary[];
  reports: AgentCoreReportSummary[];
  consultations: AgentCoreConsultationSummary[];
};

export type AgentCoreResolveResponse = {
  patient?: AgentCorePatientSnapshot;
};

export type AgentCoreThreadSummary = {
  id: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  title: string;
  lastMessagePreview: string;
  creatorID?: string;
  /** Empty/missing patientID marks a general (patient-less) thread. */
  patientID?: string;
  archivedAt?: string;
  archivedBy?: string;
  unarchivedAt?: string;
  unarchivedBy?: string;
  /** Entity this conversation is about; absent = patient-level thread. */
  subject?: AgentThreadSubject;
};

export type AgentCoreDocumentArtifact = {
  artifactID: string;
  documentID: string;
  title: string;
  action: 'created' | 'updated';
  assetCount: number;
  revision: number;
};

/** Applied Transcriptor catalog change: a page-refresh signal, not chat
 * content — the client reacts by refreshing templates/schemas and opening
 * created resources. */
export type AgentCoreCatalogArtifact = {
  artifactID: string;
  kind: 'template' | 'schema';
  resourceID: string;
  seriesID?: string;
  title: string;
  action: 'created' | 'updated';
};

export type AgentCoreApprovalRequest = {
  artifactID: string;
  proposalID: string;
  kind: string;
  title: string;
  preview: Record<string, unknown>;
};

export type AgentCoreApproval = {
  proposalID: string;
  decision: 'approve' | 'disapprove' | 'feedback';
};

export type AgentCoreActivity = {
  id: string;
  kind:
    | 'working'
    | 'search_records'
    | 'read_report'
    | 'read_consultation'
    | 'compare_reports'
    | 'inspect_images'
    | 'list_documents'
    | 'read_document'
    | 'create_document'
    | 'update_document'
    | 'analyze_patient_data';
  state: 'running' | 'completed' | 'failed';
  data: Record<string, string>;
  errorCode?: string;
};

export type AgentCoreMessageContext = AgentCoreContext & {
  label?: string;
  resources: { type: string; id: string; name?: string }[];
};

export type AgentCoreConversationMessage =
  | {
      role: 'user' | 'assistant';
      content: string;
      authorID?: string;
      attachments?: AgentCoreAttachment[];
      approval?: AgentCoreApproval;
      createdAt?: string;
      messageContext?: AgentCoreMessageContext;
    }
  | {
      role: 'document';
      artifact: AgentCoreDocumentArtifact;
    }
  | {
      role: 'catalog';
      artifact: AgentCoreCatalogArtifact;
    }
  | { role: 'approval'; approval: AgentCoreApprovalRequest }
  | {
      role: 'activity';
      activity: AgentCoreActivity;
    };

export type AgentCoreAttachment = {
  assetID: string;
  name: string;
  mime: string;
  size: number;
  sha256: string;
};

export type AgentCoreThreadDetail = {
  thread?: AgentCoreThreadSummary;
  messages: AgentCoreConversationMessage[];
};

export type AgentCoreRunEvent =
  | {
      type: 'started';
      run_id: string;
      thread_id: string;
      planned_tool_ids: string[];
    }
  | {
      type: 'activity';
      activity: AgentCoreActivity;
    }
  | {
      type: 'artifact';
      artifact:
        | AgentCoreDocumentArtifact
        | AgentCoreApprovalRequest
        | AgentCoreCatalogArtifact;
    }
  | {
      type: 'message_delta' | 'message_completed';
      content: string;
    }
  | {
      type: 'completed';
      run_id: string;
      thread_id: string;
      used_tool_ids: string[];
    }
  | {
      type: 'failed';
      code?: string;
      message: string;
    }
  | {
      type: 'unknown';
      message: string;
    };

export class AgentCoreApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public code?: string,
  ) {
    super(message);
    this.name = 'AgentCoreApiError';
  }
}

const toAgentCoreApiError = (error: unknown): AgentCoreApiError => {
  const connectError = ConnectError.from(error);
  // @bufbuild/connect's Code is a numeric enum (Code.NotFound === 5), so
  // toString() yields "5" while callers compare against names like
  // 'NotFound'. Normalize to the canonical enum name.
  const codeName =
    typeof connectError.code === 'string'
      ? connectError.code
      : (Code[connectError.code as Code] as string | undefined) ??
        String(connectError.code);
  return new AgentCoreApiError(
    connectError.rawMessage || connectError.message,
    0,
    codeName,
  );
};

const toProtoContext = (context: AgentCoreContext): RunContext =>
  new RunContext({
    PatientID: context.patient_id ?? '',
    OrganizationID: context.organization_id ?? '',
  });

const toProtoUserMessage = (
  message: string,
  attachments?: AgentCoreAttachment[],
  pageContext?: AgentCorePageContext,
  approval?: AgentCoreApproval,
): UserMessage => {
  const blocks: UserMessageBlock[] = [
    new UserMessageBlock({
      Content: {
        case: 'Text',
        value: new UserTextBlock({
          Text: message,
        }),
      },
    }),
  ];
  for (const attachment of attachments ?? []) {
    blocks.push(
      new UserMessageBlock({
        Content: {
          case: 'File',
          value: new UserFileBlock({
            AssetID: attachment.assetID,
            Name: attachment.name,
            MIME: attachment.mime,
            Size: BigInt(attachment.size),
            SHA256: attachment.sha256,
          }),
        },
      }),
    );
  }
  return new UserMessage({
    Blocks: blocks,
    PageContext: pageContext
      ? new PageContext({
          Page: pageContext.page,
          Path: pageContext.path ?? '',
          DOM: pageContext.dom ?? '',
          Resources: pageContext.resources.map(
            (resource) =>
              new PageResource({ Type: resource.type, ID: resource.id }),
          ),
        })
      : undefined,
    Approval: approval
      ? new UserApproval({
          ProposalID: approval.proposalID,
          Decision: {
            approve: UserApproval_ApprovalDecision.DecisionApprove,
            disapprove: UserApproval_ApprovalDecision.DecisionDisapprove,
            feedback: UserApproval_ApprovalDecision.DecisionFeedback,
          }[approval.decision],
        })
      : undefined,
  });
};

export const resolveAgentCoreContext = async (
  _actor: AgentCoreActor,
  context: AgentCoreContext,
): Promise<AgentCoreResolveResponse> => {
  void context;
  return {};
};

export const streamAgentCoreRun = async (args: {
  actor: AgentCoreActor;
  context: AgentCoreContext;
  message: string;
  attachments?: AgentCoreAttachment[];
  pageContext?: AgentCorePageContext;
  approval?: AgentCoreApproval;
  threadID?: string;
  subject?: AgentThreadSubject | null;
  signal?: AbortSignal;
  onEvent: (event: AgentCoreRunEvent) => void;
}) => {
  const {
    context,
    message,
    attachments,
    pageContext,
    approval,
    threadID,
    subject,
    signal,
    onEvent,
  } = args;

  try {
    const stream = api.agent.run(
      {
        Context: toProtoContext(context),
        Message: toProtoUserMessage(
          message,
          attachments,
          pageContext,
          approval,
        ),
        ThreadID: threadID ?? '',
        ...toProtoThreadSubjectFields(subject),
      },
      { signal },
    );

    for await (const event of stream) {
      onEvent(toAgentCoreRunEvent(event));
    }
  } catch (error) {
    throw toAgentCoreApiError(error);
  }
};

export const listAgentCoreThreads = async (
  _actor: AgentCoreActor,
  context: AgentCoreContext,
): Promise<AgentCoreThreadSummary[]> => {
  try {
    const response = await api.agent.listThreads({
      Context: toProtoContext(context),
      Limit: 50,
    });

    return response.Threads.map(toAgentCoreThreadSummary);
  } catch (error) {
    throw toAgentCoreApiError(error);
  }
};

export const createAgentCoreThread = async (
  _actor: AgentCoreActor,
  context: AgentCoreContext,
  subject?: AgentThreadSubject | null,
): Promise<AgentCoreThreadSummary> => {
  try {
    const response = await api.agent.createThread({
      Context: toProtoContext(context),
      ...toProtoThreadSubjectFields(subject),
    });
    if (!response.Thread?.ID) {
      throw new Error('The conversation could not be created.');
    }
    return toAgentCoreThreadSummary(response.Thread);
  } catch (error) {
    throw toAgentCoreApiError(error);
  }
};

export const getAgentCoreThread = async (
  _actor: AgentCoreActor,
  _context: AgentCoreContext,
  threadID: string,
): Promise<AgentCoreThreadDetail> => {
  try {
    return toAgentCoreThreadDetail(
      await api.agent.getThread({
        ThreadID: threadID,
        MessageLimit: 120,
      }),
    );
  } catch (error) {
    throw toAgentCoreApiError(error);
  }
};

const toAgentCoreThreadSummary = (
  item: AgentThread,
): AgentCoreThreadSummary => ({
  id: item.ID,
  status: item.Status,
  createdAt: item.CreatedAt,
  updatedAt: item.UpdatedAt,
  title: item.Title,
  lastMessagePreview: item.LastMessagePreview,
  creatorID: item.CreatorID || undefined,
  patientID: item.PatientID || undefined,
  archivedAt: item.ArchivedAt || undefined,
  archivedBy: item.ArchivedBy || undefined,
  unarchivedAt: item.UnarchivedAt || undefined,
  unarchivedBy: item.UnarchivedBy || undefined,
  subject:
    item.SubjectType && item.SubjectID
      ? toAgentThreadSubject(item.SubjectType, item.SubjectID)
      : undefined,
});

export const archiveAgentCoreThread = async (
  _actor: AgentCoreActor,
  _context: AgentCoreContext,
  threadID: string,
  archive: boolean,
): Promise<AgentCoreThreadSummary | undefined> => {
  try {
    const response = await api.agent.archiveThread({
      ThreadID: threadID,
      Archive: archive,
    });
    return response.Thread
      ? toAgentCoreThreadSummary(response.Thread)
      : undefined;
  } catch (error) {
    throw toAgentCoreApiError(error);
  }
};

const toAgentCoreThreadDetail = (
  resp: GetAgentThreadResp,
): AgentCoreThreadDetail => ({
  thread: resp.Thread ? toAgentCoreThreadSummary(resp.Thread) : undefined,
  messages: resp.Messages.map(toAgentCoreConversationMessage).filter(
    (item): item is AgentCoreConversationMessage => Boolean(item),
  ),
});

const toAgentCoreConversationMessage = (
  item: ConversationMessage,
): AgentCoreConversationMessage | null => {
  if (item.Activity) {
    return { role: 'activity', activity: toAgentCoreActivity(item.Activity) };
  }
  if (item.Artifact) {
    // Route on the proto discriminant, not on field presence: a future
    // artifact variant that happens to carry `proposalID` must not be
    // misrouted into the approval timeline.
    switch (item.Artifact.Kind) {
      case AgentArtifact_ArtifactKind.KindApprovalRequest:
        return {
          role: 'approval',
          approval: toAgentCoreApprovalArtifact(item.Artifact),
        };
      case AgentArtifact_ArtifactKind.KindDocumentTemplate:
      case AgentArtifact_ArtifactKind.KindEncounterSchema:
        return {
          role: 'catalog',
          artifact: toAgentCoreCatalogArtifact(item.Artifact),
        };
      default:
        return {
          role: 'document',
          artifact: toAgentCoreDocumentArtifact(item.Artifact),
        };
    }
  }
  if (item.Role === ConversationMessage_MessageRole.RoleUser) {
    return {
      role: 'user',
      content: item.Content,
      authorID: item.AuthorID || undefined,
      attachments: (item.Attachments ?? []).map((file) => ({
        assetID: file.AssetID,
        name: file.Name,
        mime: file.MIME,
        size: Number(file.Size),
        sha256: file.SHA256,
      })),
      createdAt: item.CreatedAt,
      messageContext: item.Context
        ? {
            patient_id: item.Context.PatientID,
            organization_id: item.Context.OrganizationID,
            label: item.ContextLabel,
            resources: (item.PageContext?.Resources ?? []).map((resource) => ({
              type: resource.Type,
              id: resource.ID,
              name: resource.Name,
            })),
          }
        : undefined,
      approval: item.Approval
        ? {
            proposalID: item.Approval.ProposalID,
            decision:
              item.Approval.Decision ===
              UserApproval_ApprovalDecision.DecisionApprove
                ? 'approve'
                : item.Approval.Decision ===
                    UserApproval_ApprovalDecision.DecisionFeedback
                  ? 'feedback'
                  : 'disapprove',
          }
        : undefined,
    };
  }

  if (item.Role === ConversationMessage_MessageRole.RoleAssistant) {
    return {
      role: 'assistant',
      content: item.Content,
      authorID: item.AuthorID || undefined,
      createdAt: item.CreatedAt,
    };
  }

  return null;
};

const activityKinds: Record<number, AgentCoreActivity['kind']> = {
  [AgentActivity_ActivityKind.KindWorking]: 'working',
  [AgentActivity_ActivityKind.KindSearchRecords]: 'search_records',
  [AgentActivity_ActivityKind.KindReadReport]: 'read_report',
  [AgentActivity_ActivityKind.KindReadConsultation]: 'read_consultation',
  [AgentActivity_ActivityKind.KindCompareReports]: 'compare_reports',
  [AgentActivity_ActivityKind.KindInspectImages]: 'inspect_images',
  [AgentActivity_ActivityKind.KindListDocuments]: 'list_documents',
  [AgentActivity_ActivityKind.KindReadDocument]: 'read_document',
  [AgentActivity_ActivityKind.KindCreateDocument]: 'create_document',
  [AgentActivity_ActivityKind.KindUpdateDocument]: 'update_document',
  [AgentActivity_ActivityKind.KindAnalyzePatientData]: 'analyze_patient_data',
};

const activityStates: Record<number, AgentCoreActivity['state']> = {
  [AgentActivity_ActivityState.StateRunning]: 'running',
  [AgentActivity_ActivityState.StateCompleted]: 'completed',
  [AgentActivity_ActivityState.StateFailed]: 'failed',
};

const toAgentCoreActivity = (activity: AgentActivity): AgentCoreActivity => ({
  id: activity.ID,
  kind: activityKinds[activity.Kind] ?? 'working',
  state: activityStates[activity.State] ?? 'running',
  data: activity.Data,
  errorCode: activity.ErrorCode || undefined,
});

const toAgentCoreApprovalArtifact = (
  artifact: AgentArtifact,
): AgentCoreApprovalRequest => {
  if (artifact.Kind !== AgentArtifact_ArtifactKind.KindApprovalRequest) {
    throw new Error('approval artifact converter called for another kind');
  }
  let preview: Record<string, unknown> = {};
  try {
    preview = JSON.parse(artifact.Data.preview_json || '{}') as Record<
      string,
      unknown
    >;
  } catch {
    preview = {};
  }
  return {
    artifactID: artifact.ID,
    proposalID: artifact.ResourceID,
    kind: artifact.Data.change_kind || 'change',
    title: artifact.Title,
    preview,
  };
};

const toAgentCoreCatalogArtifact = (
  artifact: AgentArtifact,
): AgentCoreCatalogArtifact => {
  return {
    artifactID: artifact.ID,
    kind:
      artifact.Kind === AgentArtifact_ArtifactKind.KindDocumentTemplate
        ? 'template'
        : 'schema',
    resourceID: artifact.ResourceID,
    seriesID: artifact.Data.series_id || undefined,
    title: artifact.Title,
    action:
      artifact.Action === AgentArtifact_ArtifactAction.ActionCreated
        ? 'created'
        : 'updated',
  };
};

const toAgentCoreDocumentArtifact = (
  artifact: AgentArtifact,
): AgentCoreDocumentArtifact => {
  return {
    artifactID: artifact.ID,
    documentID: artifact.ResourceID,
    title: artifact.Title,
    action:
      artifact.Action === AgentArtifact_ArtifactAction.ActionCreated
        ? 'created'
        : 'updated',
    assetCount: Number(artifact.Data.asset_count || 0),
    revision: Number(artifact.Data.revision || 0),
  };
};

const toAgentCoreArtifact = (
  artifact: AgentArtifact,
):
  | AgentCoreDocumentArtifact
  | AgentCoreApprovalRequest
  | AgentCoreCatalogArtifact => {
  switch (artifact.Kind) {
    case AgentArtifact_ArtifactKind.KindApprovalRequest:
      return toAgentCoreApprovalArtifact(artifact);
    case AgentArtifact_ArtifactKind.KindDocumentTemplate:
    case AgentArtifact_ArtifactKind.KindEncounterSchema:
      return toAgentCoreCatalogArtifact(artifact);
    default:
      return toAgentCoreDocumentArtifact(artifact);
  }
};

const toAgentCoreRunEvent = (resp: RunAgentResp): AgentCoreRunEvent => {
  switch (resp.Event.case) {
    case 'Started':
      return {
        type: 'started',
        run_id: resp.Event.value.RunID,
        thread_id: resp.Event.value.ThreadID,
        planned_tool_ids: resp.Event.value.PlannedToolIDs,
      };
    case 'Activity':
      return {
        type: 'activity',
        activity: toAgentCoreActivity(resp.Event.value),
      };
    case 'Artifact':
      return {
        type: 'artifact',
        artifact: toAgentCoreArtifact(resp.Event.value),
      };
    case 'MessageDelta':
      return {
        type: 'message_delta',
        content: resp.Event.value.Content,
      };
    case 'MessageCompleted':
      return {
        type: 'message_completed',
        content: resp.Event.value.Content,
      };
    case 'Completed':
      return {
        type: 'completed',
        run_id: resp.Event.value.RunID,
        thread_id: resp.Event.value.ThreadID,
        used_tool_ids: resp.Event.value.UsedToolIDs,
      };
    case 'Failed':
      return {
        type: 'failed',
        code: resp.Event.value.Code,
        message: resp.Event.value.Message,
      };
    default:
      return {
        type: 'unknown',
        message: 'unknown event',
      };
  }
};

const computeFileSHA256 = async (file: File): Promise<string> => {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    await file.arrayBuffer(),
  );
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');
};

/**
 * Uploads one chat attachment through the core upload-session pipeline:
 * start session (ChatUpload, thread attribution) -> presigned PUT ->
 * finish -> close. The asset ID is pre-allocated by core at session start,
 * so the finalized asset is known without polling.
 */
export const uploadChatAttachment = async (
  file: File,
  threadID: string,
  patientID?: string,
  organizationID?: string,
): Promise<AgentCoreAttachment> => {
  const sha256 = await computeFileSHA256(file);
  const startResponse = await api.storage.startUploadSession(
    new StartUploadSessionReq({
      Upload: {
        case: 'ChatUpload',
        value: {
          FilePath: file.name,
          ThreadID: threadID,
          ...(patientID ? { PatientID: patientID } : {}),
          ...(organizationID ? { OrganizationID: organizationID } : {}),
        },
      },
    }),
  );
  const sessionID = startResponse.UploadSession?.ID ?? '';
  const target = startResponse.UploadTargets?.[0];
  const assetID = startResponse.AssetID ?? '';
  if (!sessionID || !target?.UploadURL || !target.FileID || !assetID) {
    throw new Error('The upload could not be started.');
  }
  await api.storage.notifyFileUploadStarted(
    new NotifyFileUploadStartedReq({ FileID: target.FileID }),
  );
  const uploadResponse = await fetch(target.UploadURL, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
    },
    body: file,
  });
  if (!uploadResponse.ok) {
    throw new Error('The file could not be uploaded.');
  }
  await api.storage.notifyFileUploadFinished(
    new NotifyFileUploadFinishedReq({ FileID: target.FileID }),
  );
  await api.storage.closeSession(
    new CloseSessionReq({ UploadSessionID: sessionID }),
  );
  return {
    assetID,
    name: file.name,
    mime: file.type || 'application/octet-stream',
    size: file.size,
    sha256,
  };
};
